/**
 * The agent catalogue, read once into the shape the app's agent code needs.
 *
 * `src/config/pms_ui_discovery_summary.json` is the single source of truth for
 * what the agent can do in the app, and the same file the backend loads into
 * its prompts. It follows Ki_UI's `ui_discovery_summary.json` convention key for
 * key, which is written for a model to read: prose params, a `tab_control`
 * block for enumerated params, `redux` / `submit` / `reRunAnalysis` blocks
 * naming action types. This module turns that into plain lookups — route
 * params, enumerated values, declared actions, seats — so navigation, the
 * request store and the screen context cannot read the file three different
 * ways. Two prose conventions of the file are read here and nowhere else: the
 * default an enumerated param states ("Default monthly.") and a list param
 * ("list — …", comma-separated in the URL).
 *
 * Pure: no React, no DOM, no store. Tolerant of a malformed file (an entry
 * missing a block is skipped, not thrown on): `scripts/check-ui-discovery.mjs`
 * is where a bad file fails, with a reason.
 */
import discovery from '../../config/pms_ui_discovery_summary.json';
import { allowedScreens, SEAT_KEYS } from '../../config/navigation';
import { LEGACY_PARAM, LEGACY_SCREEN_TAB, workspaceOf } from '../../config/desk';

export const DISCOVERY = discovery;
export const AGENT_CONTRACT = 'pms-agent/1';

/** Signals handled on every screen rather than only on the screen that declares them. */
export const GLOBAL_SIGNALS = Object.freeze(['REQUEST_WATCHLIST_SUBMIT']);

/**
 * Screens that only forward. `/clients` is a `<Navigate>` to the Dashboard in
 * src/router.js, which drops the query string; the client book is the
 * Dashboard's clients tab. The agent is sent straight there, and a seat may
 * open `clients` only if it may open where it lands.
 */
const forward = (screen, params, map) => Object.freeze({
  screen, params: Object.freeze(params), ...(map ? { map: Object.freeze(map) } : {}),
});
/* The Alpha Desk (config/desk.js) replaced the stock-alpha pages and the stock
   master's own page: each old screen is a tab now, and the agent is sent
   straight to it with its params under the names the desk reads. Built from
   the same map the old routes redirect by, so the two cannot disagree. */
const FORWARDS = Object.freeze({
  clients: forward('dashboard', { tab: 'clients' }),
  ...Object.fromEntries(Object.entries(LEGACY_SCREEN_TAB).map(([id, [ws, tab]]) => [
    `stockAlpha${id[0].toUpperCase()}${id.slice(1)}`,
    forward(workspaceOf(ws).key, { tab }, LEGACY_PARAM),
  ])),
  /* StockDetail { symbol, tab } is the desk's Fundamentals tab, whose own
     sections keep the old tab ids under `section`. */
  stock: forward('deskStock', { tab: 'fundamentals' }, { tab: 'section' }),
});

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const strings = (list) => (Array.isArray(list) ? list.filter((v) => typeof v === 'string') : []);

/** Entry blocks that name an `UPDATE_*` form action, and those that name a `REQUEST_*` signal. */
const isReduxBlock = (name) => name === 'redux' || /Redux$/.test(name);
const isSignalBlock = (name) => name === 'reRunAnalysis' || name === 'submit' || /Submit$/.test(name);

/** `{ param: values[] }` from either `tab_control` form: `{ param, values }` or `{ params, <p>_values }`. */
function enumeratedValues(tabControl) {
  const out = {};
  if (!isPlainObject(tabControl)) return out;
  if (typeof tabControl.param === 'string') {
    const values = strings(tabControl.values);
    if (values.length) out[tabControl.param] = Object.freeze(values);
  }
  strings(tabControl.params).forEach((param) => {
    const values = strings(tabControl[`${param}_values`]);
    if (values.length) out[param] = Object.freeze(values);
  });
  return out;
}

/**
 * The default an enumerated param's prose states ("… Default monthly." or, for
 * a list, "Default 1y, 3y, inception."), or null. Only a default made wholly of
 * listed values counts, so a sentence such as "Default the largest book" is
 * never mistaken for one.
 */
function statedDefault(prose, values) {
  const m = /\bDefault:?\s+([^.;(]+)/.exec(prose || '');
  if (!m || !values) return null;
  const items = m[1].split(',').map((v) => v.trim().replace(/^['"]|['"]$/g, '')).filter(Boolean);
  return items.length && items.every((v) => values.includes(v)) ? items.join(',') : null;
}

/** A list param's prose says so up front: "Optional. list — …" (comma-separated in the URL). */
const isListProse = (prose) => /^(?:Optional\.\s*)?list\b/i.test(String(prose || '').trim());

/** The action types an entry's blocks declare, in entry order. */
function declaredBy(entry, isBlock) {
  const out = [];
  Object.entries(entry).forEach(([name, block]) => {
    if (!isBlock(name) || !isPlainObject(block) || typeof block.action !== 'string' || !block.action) return;
    if (!out.includes(block.action)) out.push(block.action);
  });
  return out;
}

const RAW = new Map();
const BUILT = {};
const DECLARED = new Set();

Object.entries(isPlainObject(discovery?.sections) ? discovery.sections : {}).forEach(([group, section]) => {
  if (!isPlainObject(section)) return;
  Object.entries(section).forEach(([key, entry]) => {
    const nav = isPlainObject(entry) ? entry.navigation : null;
    if (!isPlainObject(nav) || typeof nav.screen !== 'string' || typeof nav.route !== 'string') return;

    const routeParams = (nav.route.match(/:(\w+)/g) || []).map((s) => s.slice(1));
    const params = {};
    Object.entries(isPlainObject(nav.params) ? nav.params : {}).forEach(([name, prose]) => {
      params[name] = typeof prose === 'string' ? prose : '';
    });
    const values = enumeratedValues(entry.tab_control);
    const defaults = {};
    Object.entries(values).forEach(([name, list]) => {
      const stated = statedDefault(params[name], list);
      if (stated) defaults[name] = stated;
    });
    const signals = declaredBy(entry, isSignalBlock);
    const reduxActions = declaredBy(entry, isReduxBlock);
    [...signals, ...reduxActions, ...strings(entry.actions?.action_types)].forEach((type) => DECLARED.add(type));

    RAW.set(key, entry);
    BUILT[key] = Object.freeze({
      key,
      group,
      screen: nav.screen,
      route: nav.route,
      sourceFile: typeof nav.source_file === 'string' ? nav.source_file : null,
      requiredParams: Object.freeze(strings(nav.required_params)),
      routeParams: Object.freeze(routeParams),
      queryParams: Object.freeze(Object.keys(params).filter((name) => !routeParams.includes(name))),
      params: Object.freeze(params),
      values: Object.freeze(values),
      defaults: Object.freeze(defaults),
      lists: Object.freeze(Object.keys(params).filter((name) => isListProse(params[name]))),
      signals: Object.freeze(signals),
      reduxActions: Object.freeze(reduxActions),
      note: typeof nav.note === 'string' ? nav.note : '',
      notes: typeof entry.notes === 'string' ? entry.notes : '',
    });
  });
});

/** Every screen the catalogue describes, by screen key (the ROUTES id, or `reportView`). */
export const SCREENS = Object.freeze(BUILT);

/** Every action type declared anywhere in the catalogue — the executor's allowlist. */
export const DECLARED_ACTIONS = DECLARED;

const has = (key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(SCREENS, key);

/** The raw JSON entry for a screen key, or null. */
export function screenEntry(key) {
  return has(key) ? RAW.get(key) : null;
}

/** The values an enumerated param takes on a screen, or null when it is free text or undeclared. */
export function paramValues(key, param) {
  return has(key) ? SCREENS[key].values[param] || null : null;
}

/** The default an enumerated param's prose states (list defaults comma-joined), or null. */
export function paramDefault(key, param) {
  return has(key) ? SCREENS[key].defaults[param] ?? null : null;
}

/** Whether a param holds a comma-separated list (its prose starts "list —"). */
export function isListParam(key, param) {
  return has(key) && SCREENS[key].lists.includes(param);
}

/** The `REQUEST_*` signals a screen declares (`reRunAnalysis`, `submit`, `*Submit`). */
export function screenSignals(key) {
  return has(key) ? SCREENS[key].signals : [];
}

/** The `UPDATE_*` form actions a screen declares (`redux`, `*Redux`). */
export function screenReduxActions(key) {
  return has(key) ? SCREENS[key].reduxActions : [];
}

/** Where a forwarding screen really lands, `{ screen, params, map? }`, or null. */
export function screenForward(key) {
  return has(key) ? FORWARDS[key] || null : null;
}

/**
 * The seats that can open a screen, in SEAT_KEYS order.
 *
 * The shell's own answer (`allowedScreens`), so the agent is never told a
 * screen is open to a seat the guard would bounce. The report viewer is open
 * to every seat; a forwarding screen only to seats that may also open where it
 * lands (the PMS seat is allowed `/clients` but not the Dashboard behind it).
 */
export function screenSeats(key) {
  if (!has(key)) return [];
  if (key === 'reportView') return [...SEAT_KEYS];
  const target = FORWARDS[key]?.screen;
  return SEAT_KEYS.filter((seat) => {
    const allowed = allowedScreens(seat);
    return allowed.has(key) && (!target || allowed.has(target));
  });
}
